import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20">
      <div className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#1E3A8A]">Contact</div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 mt-3">Get in touch</h1>
      <p className="mt-4 text-slate-600 max-w-xl">
        Whether you have a single overseas case or a rolling volume — our team will get
        back to you within one business day.
      </p>
      <div className="mt-5 inline-flex items-start sm:items-center gap-3 border border-slate-200 rounded-2xl sm:rounded-full bg-white px-5 py-3 max-w-full">
        <Mail size={18} className="text-[#1E3A8A] shrink-0 mt-0.5 sm:mt-0" />
        <a href="mailto:verificationservices@theglobalchecks.com" className="font-semibold text-slate-900 hover:text-[#1E3A8A] break-all" data-testid="contact-email-link">
          verificationservices@theglobalchecks.com
        </a>
      </div>

      <div className="mt-12 grid lg:grid-cols-[1fr_1.3fr] gap-8">
        <div className="space-y-6">
          <div className="border border-slate-200 rounded-2xl p-6 bg-white">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-slate-500 mb-4">Our offices</div>
            <div className="space-y-4">
              {[{ c: "Noida", tag: "North India Operations" }, { c: "Bengaluru", tag: "Tech & Overseas Liaison" }].map((o) => (
                <div key={o.c} className="flex items-start gap-3">
                  <MapPin size={18} className="text-[#1E3A8A] mt-1" />
                  <div>
                    <div className="font-semibold text-slate-900">{o.c}, India</div>
                    <div className="text-xs text-slate-500">{o.tag}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="border border-slate-200 rounded-2xl bg-white p-7 space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" required value={form.name} onChange={update("name")} data-testid="contact-name-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={update("email")} data-testid="contact-email-input" className="mt-1.5" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input id="phone" value={form.phone} onChange={update("phone")} data-testid="contact-phone-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input id="subject" required value={form.subject} onChange={update("subject")} data-testid="contact-subject-input" className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label htmlFor="message">Message</Label>
            <Textarea id="message" required rows={5} value={form.message} onChange={update("message")} data-testid="contact-message-input" className="mt-1.5" />
          </div>
          <Button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="pill-btn bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-11">
            {submitting ? "Sending…" : "Send message"}
          </Button>
        </form>
      </div>
    </div>
  );
}
